import { intranetNewsPath, type IntranetFeedItem } from '@gb-ticket/shared-types';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { IntranetContentMenu } from '@/products/intranet/components/intranet-content-menu';
import { coverSrc } from '@/products/intranet/features/intranet/cover-src';
import {
  highlightStripDragPx,
  highlightStripMetrics,
  highlightStripSnap,
  highlightStripState,
  highlightStripTrackShift,
  sortHighlightItems,
} from '@/products/intranet/features/intranet/highlight-strip';

const STRIP_GAP_PX = 12;
const DRAG_LOCK_PX = 8;
const SNAP_TRANSITION = 'transform 520ms cubic-bezier(0.16, 1, 0.3, 1)';

export function IntranetHighlightStrip({
  items,
  desktop,
}: {
  items: readonly IntranetFeedItem[];
  desktop: boolean;
}) {
  const [start, setStart] = useState(0);
  const [dragPx, setDragPx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef<HTMLUListElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    x: number;
    y: number;
    at: number;
    lastX: number;
    lastAt: number;
    locked: boolean;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);
  const ordered = sortHighlightItems(items);
  const state = highlightStripState(ordered.length, start, desktop);
  if (ordered.length === 0) {
    return null;
  }
  const metrics = highlightStripMetrics(state.fullSlots);
  const maxStart = Math.max(0, ordered.length - state.fullSlots);
  const shift = highlightStripTrackShift(
    state.startIndex,
    state.peekLeft,
    metrics.cardWidth,
    metrics.peekWidth ?? '0px',
  );
  const visualDrag = highlightStripDragPx(dragPx, state.startIndex, maxStart);
  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function measureStep() {
    const card = trackRef.current?.querySelector('li');
    const width = card?.getBoundingClientRect().width ?? 0;
    return width > 0 ? width + STRIP_GAP_PX : 0;
  }

  function onPointerDown(event: React.PointerEvent<HTMLUListElement>) {
    if (desktop || maxStart === 0) {
      return;
    }
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    const now = event.timeStamp;
    dragRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      at: now,
      lastX: event.clientX,
      lastAt: now,
      locked: false,
      moved: false,
    };
  }

  function onPointerMove(event: React.PointerEvent<HTMLUListElement>) {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.locked) {
      if (Math.hypot(dx, dy) < DRAG_LOCK_PX) {
        return;
      }
      if (Math.abs(dy) > Math.abs(dx)) {
        dragRef.current = null;
        return;
      }
      drag.locked = true;
      drag.moved = true;
      setDragging(true);
      try {
        trackRef.current?.setPointerCapture(event.pointerId);
      } catch {
        /* jsdom */
      }
    }
    event.preventDefault();
    drag.lastX = event.clientX;
    drag.lastAt = event.timeStamp;
    setDragPx(dx);
  }

  function endDrag(event: React.PointerEvent<HTMLUListElement>) {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    const dx = event.clientX - drag.x;
    const elapsed = Math.max(1, event.timeStamp - drag.lastAt);
    const flick = (event.clientX - drag.lastX) / elapsed;
    const travel = dx / Math.max(1, event.timeStamp - drag.at);
    const velocity = Math.abs(flick) >= Math.abs(travel) ? flick : travel;
    if (drag.moved) {
      suppressClickRef.current = true;
      setStart(
        highlightStripSnap({
          startIndex: state.startIndex,
          maxStart,
          dragPx: dx,
          stepPx: measureStep(),
          velocityPxPerMs: velocity,
        }),
      );
    }
    dragRef.current = null;
    setDragPx(0);
    setDragging(false);
  }

  return (
    <section aria-label="Highlight" className="space-y-2">
      <h2 className="text-sm font-medium text-muted-foreground">Highlight</h2>
      <div className="relative">
        <div className="overflow-hidden px-2 py-2">
          <ul
            ref={trackRef}
            className="flex w-full touch-pan-y gap-3"
            style={{
              transform: `translate3d(calc(${shift} + ${visualDrag}px), 0, 0)`,
              transition: dragging || reduceMotion ? 'none' : SNAP_TRANSITION,
              willChange: dragging ? 'transform' : 'auto',
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onClickCapture={(event) => {
              if (!suppressClickRef.current) {
                return;
              }
              event.preventDefault();
              event.stopPropagation();
              suppressClickRef.current = false;
            }}
          >
            {ordered.map((item, index) => {
              const showMenu =
                index >= state.startIndex && index < state.startIndex + state.fullSlots;
              return (
                <li key={item.id} className="shrink-0" style={{ width: metrics.cardWidth }}>
                  <HighlightCard item={item} showMenu={showMenu} />
                </li>
              );
            })}
          </ul>
        </div>
        {state.showPrev ? (
          <Button
            type="button"
            size="icon"
            variant="secondary"
            className="absolute top-1/2 left-2 z-40 -translate-y-1/2 rounded-full shadow"
            aria-label="Vorherige Highlights"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setStart(state.startIndex - 1);
            }}
          >
            <ChevronLeftIcon className="size-4" aria-hidden />
          </Button>
        ) : null}
        {state.showNext ? (
          <Button
            type="button"
            size="icon"
            variant="secondary"
            className="absolute top-1/2 right-2 z-40 -translate-y-1/2 rounded-full shadow"
            aria-label="Weitere Highlights"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setStart(state.startIndex + 1);
            }}
          >
            <ChevronRightIcon className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>
    </section>
  );
}

function HighlightCard({ item, showMenu }: { item: IntranetFeedItem; showMenu: boolean }) {
  const src = coverSrc(item);
  const to = intranetNewsPath(item.slug);
  return (
    <article className="intranet-highlight-card relative aspect-[2/1] overflow-hidden rounded-xl bg-brand-navy shadow-sm">
      <Link to={to} className="absolute inset-0 z-10" aria-label={`Beitrag öffnen: ${item.title}`}>
        {src ? (
          <img
            src={src}
            alt={item.coverAlt ?? item.title}
            className="absolute inset-0 size-full object-cover"
          />
        ) : null}
      </Link>
      <div className="intranet-highlight-fade" aria-hidden />
      <p className="pointer-events-none absolute inset-x-3 bottom-3 z-20 line-clamp-2 text-sm leading-snug font-semibold text-white">
        <span className="intranet-highlight-title">{item.title}</span>
      </p>
      {showMenu ? (
        <div className="absolute top-2 right-2 z-30">
          <IntranetContentMenu item={item} overlay />
        </div>
      ) : null}
    </article>
  );
}
